// src/hooks/useKanbanLazyLoading.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { Opportunity } from '../feature-module/crm/deals/OpportunityFormHandler';
import { OpportunityFilters } from '../types/opportunity';
import { Stage } from '../types/funnels';
// Interfaces locais
interface EtapaLazyData {
  opportunities: Opportunity[];
  loading: boolean;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
  total: number;
  initialized: boolean;
  error: string | null;
}

interface UseKanbanLazyLoadingParams {
  funnelId: number;
  stages: Stage[];
  filters: {
    id_conta?: number;
    status?: string | string[];
    search?: string;
    tempo_entrega?: string;
  };
  limitPerStage?: number;
  autoInitialize?: boolean;
}

interface UseKanbanLazyLoadingResult {
  etapasData: Map<number, EtapaLazyData>;
  globalLoading: boolean;
  loadMoreEtapa: (etapaId: number) => Promise<void>;
  initializeAllStages: () => Promise<void>;
  refreshEtapa: (etapaId: number) => Promise<void>;
  refreshAll: () => Promise<void>;
  getEtapaData: (etapaId: number) => EtapaLazyData;
  updateOpportunityInEtapa: (opportunityId: string, updates: Partial<Opportunity>) => void;
  moveOpportunityBetweenEtapas: (opportunityId: string, fromEtapaId: number, toEtapaId: number) => void;
  addOpportunityToEtapa: (etapaId: number, opportunity: Opportunity) => void;
  removeOpportunityFromEtapa: (opportunityId: string, etapaId: number) => void;
}

const defaultEtapaData: EtapaLazyData = {
  opportunities: [],
  loading: false,
  hasMore: true,
  currentPage: 1,
  totalPages: 1,
  total: 0,
  initialized: false,
  error: null
};

// Função para converter resposta da API para Opportunity
const convertApiResponseToOpportunity = (apiData: any): Opportunity => {
  return {
    id: apiData.id.toString(),
    title: apiData.titulo,
    value: `R$ ${parseFloat(apiData.valor_licitacao || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })}`,
    bidDate: new Date(apiData.data_licitacao),
    status: apiData.status,
    contact: apiData.contato_nome || 'Não informado',
    contact_id: apiData.id_contato || null,
    source: apiData.origem_nome || 'Não informado',
    source_id: apiData.id_origem || null,
    funnel: apiData.funil_nome || '',
    funnel_id: apiData.id_funil || null,
    stage: apiData.etapa_nome || '',
    stage_id: apiData.id_etapa || null,
    owner: {
      id: apiData.id_dono || 1,
      name: apiData.dono_nome || 'Não informado',
      avatar: ''
    },
    bgColor: 'bg-primary',
    municipio: {
      id: apiData.id_municipio || 1,
      nome: apiData.nome_municipio || 'Não informado',
      uf: apiData.municipio_uf || 'SP'
    },
    numero_contrato: apiData.numero_contrato || 'Não informado',
    url_site_externo: apiData.url_site_externo || '',
    uasb: apiData.uasb || '',
    tempo_entrega: apiData.tempo_entrega || 'Sem informação',
    phone: '', // Campo obrigatório, mas não vem da API
    progress: 0 // Campo obrigatório, mas não vem da API
  };
};

export const useKanbanLazyLoading = ({
  funnelId,
  stages,
  filters,
  limitPerStage = 20,
  autoInitialize = true
}: UseKanbanLazyLoadingParams): UseKanbanLazyLoadingResult => {
  
  const [etapasData, setEtapasData] = useState<Map<number, EtapaLazyData>>(new Map());
  const [globalLoading, setGlobalLoading] = useState(false);
  
  // Refs para controle de requisições
  const loadingRefs = useRef<Set<number>>(new Set());
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const initializationRef = useRef<string>('');

  // Função para carregar dados de uma etapa específica
  const loadEtapaData = useCallback(async (
    etapaId: number, 
    page = 1, 
    append = false
  ): Promise<void> => {
    // Prevenir múltiplas requisições simultâneas para a mesma etapa
    if (loadingRefs.current.has(etapaId)) {
      console.log(`Já existe uma requisição em andamento para etapa ${etapaId}`);
      return;
    }

    // Cancelar requisição anterior se existir
    const existingController = abortControllersRef.current.get(etapaId);
    if (existingController) {
      existingController.abort();
    }

    // Criar novo AbortController
    const controller = new AbortController();
    abortControllersRef.current.set(etapaId, controller);
    loadingRefs.current.add(etapaId);

    try {
      let currentData = { ...defaultEtapaData };
      
      // Atualizar estado para mostrar loading
      setEtapasData(prev => {
        currentData = prev.get(etapaId) || { ...defaultEtapaData };
        return new Map(prev.set(etapaId, {
          ...currentData,
          loading: true,
          error: null
        }));
      });

      // Preparar filtros para a API
      const etapaFilters: any = {
        ...filters,
        id_funil: funnelId,
        id_etapa: etapaId,
        page,
        per_page: limitPerStage
      };

      console.log(`Carregando página ${page} da etapa ${etapaId}:`, etapaFilters);

      // Fazer requisição à API
      const response = await apiService.getOpportunities(etapaFilters);

      // Verificar se a requisição foi cancelada
      if (controller.signal.aborted) {
        return;
      }

      if (!response.success || !response.data) {
        console.error(`Erro na resposta da API para etapa ${etapaId}:`, response);
        throw new Error('Erro na resposta da API');
      }

      // Converter dados da API para formato interno
      const opportunities = response.data.map(convertApiResponseToOpportunity);

      // Calcular novos dados da etapa
      const newOpportunities = append 
        ? [...currentData.opportunities, ...opportunities]
        : opportunities;

      const pagination = response.pagination;
      const hasMore = pagination ? page < pagination.total_pages : false;

      // Atualizar estado da etapa
      setEtapasData(prev => new Map(prev.set(etapaId, {
        opportunities: newOpportunities,
        loading: false,
        hasMore,
        currentPage: page,
        totalPages: pagination?.total_pages || 1,
        total: pagination?.total || opportunities.length,
        initialized: true,
        error: null
      })));

      console.log(`Etapa ${etapaId} carregada: ${opportunities.length} oportunidades, página ${page}/${pagination?.total_pages || 1}`);

    } catch (error: any) {
      // Não atualizar se foi cancelado
      if (controller.signal.aborted) {
        return;
      }

      console.error(`Erro ao carregar etapa ${etapaId}:`, error);
      
      setEtapasData(prev => {
        const currentData = prev.get(etapaId) || { ...defaultEtapaData };
        return new Map(prev.set(etapaId, {
          ...currentData,
          loading: false,
          error: error.message || 'Erro ao carregar dados',
          initialized: true
        }));
      });
    } finally {
      loadingRefs.current.delete(etapaId);
      abortControllersRef.current.delete(etapaId);
    }
  }, [funnelId, filters, limitPerStage]);

  // Função para carregar mais dados de uma etapa (scroll infinito)
  const loadMoreEtapa = useCallback(async (etapaId: number): Promise<void> => {
    setEtapasData(currentEtapasData => {
      const etapaData = currentEtapasData.get(etapaId);
      
      if (!etapaData || !etapaData.hasMore || etapaData.loading) {
        return currentEtapasData;
      }

      // Chamar loadEtapaData fora do setState
      loadEtapaData(etapaId, etapaData.currentPage + 1, true);
      return currentEtapasData;
    });
  }, [loadEtapaData]);

  // Função para inicializar todas as etapas
  const initializeAllStages = useCallback(async (): Promise<void> => {
    if (!stages.length) return;

    setGlobalLoading(true);
    console.log(`Inicializando ${stages.length} etapas do funil ${funnelId}`);

    try {
      // Filtrar apenas etapas com ID válido
      const validStages = stages.filter(stage => 
        typeof stage.id === 'number' && stage.id > 0
      );

      // Carregar primeira página de cada etapa em paralelo
      const loadPromises = validStages.map(stage => 
        loadEtapaData(stage.id as number, 1, false)
      );

      await Promise.allSettled(loadPromises);

      console.log('Todas as etapas foram inicializadas');
    } catch (error) {
      console.error('Erro na inicialização das etapas:', error);
    } finally {
      setGlobalLoading(false);
    }
  }, [stages, funnelId, loadEtapaData]);

  // Função para atualizar uma etapa específica
  const refreshEtapa = useCallback(async (etapaId: number): Promise<void> => {
    console.log(`Atualizando etapa ${etapaId}`);
    
    // Resetar dados da etapa
    setEtapasData(prev => {
      const newMap = new Map(prev);
      newMap.delete(etapaId);
      return newMap;
    });

    // Recarregar primeira página
    await loadEtapaData(etapaId, 1, false);
  }, [loadEtapaData]);

  // Função para atualizar todas as etapas
  const refreshAll = useCallback(async (): Promise<void> => {
    console.log('Atualizando todas as etapas');
    
    // Cancelar todas as requisições em andamento
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    loadingRefs.current.clear();

    // Limpar dados
    setEtapasData(new Map());
    
    // Reinicializar
    await initializeAllStages();
  }, [initializeAllStages]);

  // Função para obter dados de uma etapa (com fallback)
  const getEtapaData = useCallback((etapaId: number): EtapaLazyData => {
    return etapasData.get(etapaId) || { ...defaultEtapaData };
  }, [etapasData]);

  // Função para atualizar uma oportunidade em uma etapa
  const updateOpportunityInEtapa = useCallback((
    opportunityId: string, 
    updates: Partial<Opportunity>
  ): void => {
    setEtapasData(prev => {
      const newMap = new Map(prev);
      
      // Procurar a oportunidade em todas as etapas
      Array.from(newMap.entries()).forEach(([etapaId, etapaData]) => {
        const opportunityIndex = etapaData.opportunities.findIndex(
          (opp: Opportunity) => opp.id === opportunityId
        );
        
        if (opportunityIndex !== -1) {
          const updatedOpportunities = [...etapaData.opportunities];
          updatedOpportunities[opportunityIndex] = {
            ...updatedOpportunities[opportunityIndex],
            ...updates
          };
          
          newMap.set(etapaId, {
            ...etapaData,
            opportunities: updatedOpportunities
          });
        }
      });
      
      return newMap;
    });
  }, []);

  // Função para mover oportunidade entre etapas
  const moveOpportunityBetweenEtapas = useCallback((
    opportunityId: string,
    fromEtapaId: number,
    toEtapaId: number
  ): void => {
    setEtapasData(prev => {
      const newMap = new Map(prev);
      const fromEtapa = newMap.get(fromEtapaId);
      const toEtapa = newMap.get(toEtapaId);
      
      if (!fromEtapa || !toEtapa) return prev;
      
      // Encontrar e remover da etapa origem
      const opportunityIndex = fromEtapa.opportunities.findIndex(
        (opp: Opportunity) => opp.id === opportunityId
      );
      
      if (opportunityIndex === -1) return prev;
      
      const opportunity = fromEtapa.opportunities[opportunityIndex];
      const updatedFromOpportunities = fromEtapa.opportunities.filter(
        (opp: Opportunity) => opp.id !== opportunityId
      );
      
      // Atualizar a oportunidade com nova etapa
      const updatedOpportunity = {
        ...opportunity,
        stage_id: toEtapaId,
        stage: stages.find(s => s.id === toEtapaId)?.nome || opportunity.stage
      };
      
      // Adicionar na etapa destino
      const updatedToOpportunities = [updatedOpportunity, ...toEtapa.opportunities];
      
      // Atualizar ambas as etapas
      newMap.set(fromEtapaId, {
        ...fromEtapa,
        opportunities: updatedFromOpportunities,
        total: Math.max(0, fromEtapa.total - 1)
      });
      
      newMap.set(toEtapaId, {
        ...toEtapa,
        opportunities: updatedToOpportunities,
        total: toEtapa.total + 1
      });
      
      return newMap;
    });
  }, [stages]);

  // Função para adicionar oportunidade a uma etapa
  const addOpportunityToEtapa = useCallback((
    etapaId: number, 
    opportunity: Opportunity
  ): void => {
    setEtapasData(prev => {
      const newMap = new Map(prev);
      const etapaData = newMap.get(etapaId) || { ...defaultEtapaData };
      
      newMap.set(etapaId, {
        ...etapaData,
        opportunities: [opportunity, ...etapaData.opportunities],
        total: etapaData.total + 1,
        initialized: true
      });
      
      return newMap;
    });
  }, []);

  // Função para remover oportunidade de uma etapa
  const removeOpportunityFromEtapa = useCallback((
    opportunityId: string,
    etapaId: number
  ): void => {
    setEtapasData(prev => {
      const newMap = new Map(prev);
      const etapaData = newMap.get(etapaId);
      
      if (!etapaData) return prev;
      
      const updatedOpportunities = etapaData.opportunities.filter(
        (opp: Opportunity) => opp.id !== opportunityId
      );
      
      newMap.set(etapaId, {
        ...etapaData,
        opportunities: updatedOpportunities,
        total: Math.max(0, etapaData.total - 1)
      });
      
      return newMap;
    });
  }, []);

  // Auto-inicialização quando parâmetros mudam
  useEffect(() => {
    if (!autoInitialize || !stages.length || !funnelId) return;
    
    const initKey = `${funnelId}-${stages.length}-${filters?.id_conta}-${JSON.stringify(filters?.status)}-${filters?.search}-${filters?.tempo_entrega}`;
    
    if (initializationRef.current !== initKey) {
      console.log('Parâmetros do lazy loading mudaram, reinicializando...', { funnelId, stages: stages.length, filters });
      initializationRef.current = initKey;
      initializeAllStages();
    }
  }, [autoInitialize, stages.length, funnelId, filters?.id_conta, filters?.status, filters?.search, filters?.tempo_entrega, initializeAllStages]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      // Cancelar todas as requisições pendentes
      abortControllersRef.current.forEach(controller => controller.abort());
      abortControllersRef.current.clear();
      loadingRefs.current.clear();
    };
  }, []);

  return {
    etapasData,
    globalLoading,
    loadMoreEtapa,
    initializeAllStages,
    refreshEtapa,
    refreshAll,
    getEtapaData,
    updateOpportunityInEtapa,
    moveOpportunityBetweenEtapas,
    addOpportunityToEtapa,
    removeOpportunityFromEtapa
  };
};